import React, { useState, useEffect } from 'react';
import {
  Grid, Typography, Box, Card, CardContent, Divider, LinearProgress
} from '@mui/material';
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, Legend, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar
} from 'recharts';
import { PhoneAndroidOutlined } from '@mui/icons-material';
import StatCard from '../components/StatCard';
import { getTelecomStats, getMonthlyMessages } from '../utils/api';

const TT_STYLE = { backgroundColor: '#111827', border: '1px solid rgba(0,191,165,0.2)', borderRadius: 8 };
const MTN_COLOR = '#FFB300';
const EM_COLOR  = '#00BFA5';

function CompareRow({ label, mtn, em }) {
  const total = (mtn || 0) + (em || 0);
  const mtnPct = total > 0 ? (mtn / total) * 100 : 50;
  return (
    <Box mb={2.5}>
      <Box display="flex" justifyContent="space-between" mb={0.5}>
        <Typography variant="caption" color={MTN_COLOR} fontFamily='"IBM Plex Mono", monospace' fontWeight={600}>
          {mtn?.toLocaleString()}
        </Typography>
        <Typography variant="caption" color="text.secondary" fontSize="0.7rem">{label}</Typography>
        <Typography variant="caption" color={EM_COLOR} fontFamily='"IBM Plex Mono", monospace' fontWeight={600}>
          {em?.toLocaleString()}
        </Typography>
      </Box>
      <Box position="relative" height={8} borderRadius={4} overflow="hidden" bgcolor="rgba(255,255,255,0.06)">
        <Box position="absolute" left={0} top={0} bottom={0} width={`${mtnPct}%`} bgcolor={MTN_COLOR} sx={{ borderRadius: '4px 0 0 4px', transition: 'width 0.6s ease' }} />
        <Box position="absolute" right={0} top={0} bottom={0} width={`${100 - mtnPct}%`} bgcolor={EM_COLOR} sx={{ borderRadius: '0 4px 4px 0' }} />
      </Box>
    </Box>
  );
}

import FilterBar, { EMPTY_FILTERS } from '../components/FilterBar';

export default function TelecomsPage() {
  const [telecom, setTelecom]   = useState([]);
  const [monthly, setMonthly]   = useState([]);
  const [filters, setFilters]   = useState(EMPTY_FILTERS);
  const [loading, setLoading]   = useState(true);

  useEffect(() => {
    setLoading(true);
    Promise.all([getTelecomStats(filters), getMonthlyMessages(filters)])
      .then(([t, m]) => { setTelecom(t.data); setMonthly(m.data); })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [filters]);

  const mtn = telecom.find(t => t.telecom === 'MTN') || {};
  const em  = telecom.find(t => t.telecom === 'Eswatini Mobile') || {};

  const radarData = [
    { metric: 'Patients', MTN: mtn.patients, EM: em.patients },
    { metric: 'Sent', MTN: mtn.sent, EM: em.sent },
    { metric: 'Received', MTN: mtn.received, EM: em.received },
    { metric: 'Educational', MTN: mtn.educational, EM: em.educational },
    { metric: 'Reminders', MTN: mtn.reminders, EM: em.reminders },
    { metric: 'Follow-up', MTN: mtn.followup, EM: em.followup },
  ];

  const monthlyComparison = monthly.map(m => ({
    month:    m.month,
    MTN_sent: m.mtn_sent,
    EM_sent:  m.em_sent,
    MTN_rcv:  m.mtn_received,
    EM_rcv:   m.em_received,
  }));

  return (
    <Box>
      <Box mb={3}>
        <Typography variant="h5" fontWeight={700} color="primary.main">Telecom Comparison</Typography>
        <Typography variant="body2" color="text.secondary">
          MTN (+26876 / +26878) vs Eswatini Mobile (+26879)
        </Typography>
      </Box>

      <FilterBar filters={filters} onChange={setFilters} showTelecom={false} showGroup={false} showDate={true} />

      {/* Head-to-head KPI cards */}
      <Grid container spacing={2} mb={2}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="caption" color="text.secondary" fontFamily='"IBM Plex Mono", monospace' sx={{ textTransform: 'uppercase', fontSize: '0.65rem', letterSpacing: '0.08em' }}>Patients</Typography>
              <Box display="flex" justifyContent="space-around" mt={1}>
                <Box><Typography variant="h5" color={MTN_COLOR} fontFamily='"IBM Plex Mono", monospace' fontWeight={700}>{mtn.patients?.toLocaleString()}</Typography><Typography variant="caption" color="text.secondary">MTN</Typography></Box>
                <Divider orientation="vertical" flexItem />
                <Box><Typography variant="h5" color={EM_COLOR} fontFamily='"IBM Plex Mono", monospace' fontWeight={700}>{em.patients?.toLocaleString()}</Typography><Typography variant="caption" color="text.secondary">Eswatini Mobile</Typography></Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        {[
          { label: 'Msgs Sent',      mtnV: mtn.sent,           emV: em.sent },
          { label: 'SMS Segments Out', mtnV: mtn.smsSegmentsOut, emV: em.smsSegmentsOut },
          { label: 'SMS Segments In',  mtnV: mtn.smsSegmentsIn,  emV: em.smsSegmentsIn },
          { label: 'Msgs Received',   mtnV: mtn.received,       emV: em.received },
          { label: 'Educational',     mtnV: mtn.educational,    emV: em.educational },
          { label: 'Reminders',       mtnV: mtn.reminders,      emV: em.reminders },
        ].map(item => (
          <Grid item xs={12} sm={6} md={3} key={item.label}>
            <Card>
              <CardContent sx={{ textAlign: 'center' }}>
                <Typography variant="caption" color="text.secondary" fontFamily='"IBM Plex Mono", monospace' sx={{ textTransform: 'uppercase', fontSize: '0.65rem', letterSpacing: '0.08em' }}>{item.label}</Typography>
                <Box display="flex" justifyContent="space-around" mt={1}>
                  <Box><Typography variant="h5" color={MTN_COLOR} fontFamily='"IBM Plex Mono", monospace' fontWeight={700}>{item.mtnV?.toLocaleString()}</Typography><Typography variant="caption" color="text.secondary">MTN</Typography></Box>
                  <Divider orientation="vertical" flexItem />
                  <Box><Typography variant="h5" color={EM_COLOR} fontFamily='"IBM Plex Mono", monospace' fontWeight={700}>{item.emV?.toLocaleString()}</Typography><Typography variant="caption" color="text.secondary">EM</Typography></Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Grid container spacing={2} mb={2}>
        {/* Side-by-side bar chart */}
        <Grid item xs={12} md={7}>
          <Card>
            <CardContent>
              <Typography variant="h6" mb={2}>Monthly Messages — MTN vs Eswatini Mobile</Typography>
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={monthlyComparison}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                  <XAxis dataKey="month" tick={{ fontSize: 11, fill: '#90A4AE', fontFamily: 'IBM Plex Mono' }} />
                  <YAxis tick={{ fontSize: 11, fill: '#90A4AE' }} />
                  <Tooltip contentStyle={TT_STYLE} />
                  <Legend />
                  <Bar dataKey="MTN_sent"  name="MTN Sent"          fill={MTN_COLOR} radius={[3,3,0,0]} />
                  <Bar dataKey="EM_sent"   name="Eswatini Mobile Sent" fill={EM_COLOR}  radius={[3,3,0,0]} />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>

        {/* Radar */}
        <Grid item xs={12} md={5}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="h6" mb={1}>Performance Radar</Typography>
              <ResponsiveContainer width="100%" height={260}>
                <RadarChart data={radarData}>
                  <PolarGrid stroke="rgba(255,255,255,0.1)" />
                  <PolarAngleAxis dataKey="metric" tick={{ fontSize: 11, fill: '#90A4AE', fontFamily: 'IBM Plex Mono' }} />
                  <PolarRadiusAxis tick={false} axisLine={false} />
                  <Radar name="MTN"            dataKey="MTN" stroke={MTN_COLOR} fill={MTN_COLOR} fillOpacity={0.2} />
                  <Radar name="Eswatini Mobile" dataKey="EM"  stroke={EM_COLOR}  fill={EM_COLOR}  fillOpacity={0.2} />
                  <Legend />
                  <Tooltip contentStyle={TT_STYLE} />
                </RadarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Head-to-head comparison bars */}
      <Card>
        <CardContent>
          <Box display="flex" justifyContent="space-between" mb={3}>
            <Box display="flex" alignItems="center" gap={1}>
              <Box width={12} height={12} borderRadius="50%" bgcolor={MTN_COLOR} />
              <Typography variant="caption" color={MTN_COLOR} fontFamily='"IBM Plex Mono", monospace' fontWeight={700}>MTN</Typography>
            </Box>
            <Typography variant="h6">Head-to-Head</Typography>
            <Box display="flex" alignItems="center" gap={1}>
              <Typography variant="caption" color={EM_COLOR} fontFamily='"IBM Plex Mono", monospace' fontWeight={700}>Eswatini Mobile</Typography>
              <Box width={12} height={12} borderRadius="50%" bgcolor={EM_COLOR} />
            </Box>
          </Box>
          <CompareRow label="Patients Enrolled"    mtn={mtn.patients}        em={em.patients} />
          <CompareRow label="Messages Sent"        mtn={mtn.sent}            em={em.sent} />
          <CompareRow label="SMS Segments Out"    mtn={mtn.smsSegmentsOut}  em={em.smsSegmentsOut} />
          <CompareRow label="SMS Segments In"     mtn={mtn.smsSegmentsIn}   em={em.smsSegmentsIn} />
          <CompareRow label="Messages Received"    mtn={mtn.received}        em={em.received} />
          <CompareRow label="Educational Msgs"     mtn={mtn.educational}     em={em.educational} />
          <CompareRow label="Reminder Msgs"        mtn={mtn.reminders}       em={em.reminders} />
          <CompareRow label="Follow-up Msgs"       mtn={mtn.followup}        em={em.followup} />
        </CardContent>
      </Card>
    </Box>
  );
}
